import React, { useMemo } from 'react';
import {
  AlertTriangle, Clock,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  formatDateShort, formatTimeString12Hour, formatMonthYear,
  formatWeekRange, formatHoursMinutes,
} from '../../../utils/dateUtils';
import { getLeaveTypeBgClass, getLeaveTypeBadgeClass } from '../../../utils/leaveTypeBadgeColors';

export const STATUS_CONFIG = {
  on_site:           { label: 'Present',     color: '#22c55e', light: '#dcfce7', text: '#166534' },
  work_from_home:    { label: 'WFH',         color: '#6366f1', light: '#e0e7ff', text: '#3730a3' },
  external_activity: { label: 'External',    color: '#06b6d4', light: '#cffafe', text: '#155e75' },
  on_course:         { label: 'On Course',   color: '#8b5cf6', light: '#ede9fe', text: '#5b21b6' },
  on_leave:          { label: 'On Leave',    color: '#d97706', light: '#fef3c7', text: '#78350f' },
  nightwork:         { label: 'Nightwork',   color: '#475569', light: '#f1f5f9', text: '#1e293b' },
  nightwork_rest_day:{ label: 'Recovery',    color: '#0d9488', light: '#ccfbf1', text: '#134e4a' },
  unaccounted:       { label: 'Unaccounted', color: '#dc2626', light: '#fee2e2', text: '#7f1d1d' },
  non_working_day:   { label: 'Off',         color: '#d1d5db', light: '#f9fafb', text: '#6b7280' },
  public_holiday:    { label: 'PH',          color: '#ea580c', light: '#ffedd5', text: '#7c2d12' },
};

const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const LEAVE_ABBREVIATIONS = {
  'annual leave':              'AL',
  'urgent leave':              'UL',
  'home leave':                'HL',
  'outpatient sick leave':     'MC',
  'hospitalisation leave':     'HL',
  'maternity leave':           'ML',
  'paternity leave':           'PL',
  'shared parental leave':     'SPL',
  'adoption leave':            'ADL',
  'childcare leave':           'CL',
  'extended childcare leave':  'ECL',
  'infant care leave':         'ICL',
  'marriage leave':            'MRL',
  'compassionate leave':       'CML',
  'unpaid leave':              'UPL',
  'off in lieu':               'OIL',
  'external activity':         'EA',
  'work from home':            'WFH',
  'on course':                 'OC',
  'national service':          'NS',
  'study leave':               'SL',
  'jury duty leave':           'JD',
  'medical leave':             'MC',
};

export function getLeaveTypeAbbreviation(name) {
  if (!name) return '';
  const key = name.toLowerCase().trim();
  return LEAVE_ABBREVIATIONS[key] || name.split(' ').map(w => w[0]).join('').toUpperCase();
}

const LEAVE_BG_TO_STYLE = {};
function getLeaveColorStyle(leaveTypeName) {
  if (!leaveTypeName) return null;
  const bgClass = getLeaveTypeBgClass(leaveTypeName);
  const colorMap = {
    'bg-blue-100':    { light: '#dbeafe', text: '#1e40af', color: '#3b82f6' },
    'bg-orange-100':  { light: '#ffedd5', text: '#9a3412', color: '#f97316' },
    'bg-sky-100':     { light: '#e0f2fe', text: '#075985', color: '#0ea5e9' },
    'bg-amber-100':   { light: '#fef3c7', text: '#78350f', color: '#f59e0b' },
    'bg-red-100':     { light: '#fee2e2', text: '#991b1b', color: '#ef4444' },
    'bg-pink-100':    { light: '#fce7f3', text: '#9d174d', color: '#ec4899' },
    'bg-purple-100':  { light: '#f3e8ff', text: '#6b21a8', color: '#a855f7' },
    'bg-fuchsia-100': { light: '#fae8ff', text: '#86198f', color: '#d946ef' },
    'bg-green-100':   { light: '#dcfce7', text: '#166534', color: '#22c55e' },
    'bg-emerald-100': { light: '#d1fae5', text: '#065f46', color: '#10b981' },
    'bg-rose-100':    { light: '#ffe4e6', text: '#9f1239', color: '#f43f5e' },
    'bg-gray-100':    { light: '#f3f4f6', text: '#374151', color: '#9ca3af' },
    'bg-slate-100':   { light: '#f1f5f9', text: '#334155', color: '#94a3b8' },
    'bg-teal-100':    { light: '#ccfbf1', text: '#134e4a', color: '#14b8a6' },
    'bg-cyan-100':    { light: '#cffafe', text: '#155e75', color: '#06b6d4' },
    'bg-indigo-100':  { light: '#e0e7ff', text: '#3730a3', color: '#6366f1' },
    'bg-violet-100':  { light: '#ede9fe', text: '#5b21b6', color: '#8b5cf6' },
    'bg-lime-100':    { light: '#ecfccb', text: '#3f6212', color: '#84cc16' },
    'bg-stone-100':   { light: '#f5f5f4', text: '#44403c', color: '#a8a29e' },
  };
  return colorMap[bgClass] || null;
}

// ---------------------------------------------------------------------------
// Calendar grid (enhanced with per-leave-type colours)
// ---------------------------------------------------------------------------

function formatCalendarTooltipDate(dateStr) {
  try {
    const d = new Date(`${dateStr}T12:00:00`);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-SG', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      timeZone: 'Asia/Singapore',
    });
  } catch {
    return dateStr;
  }
}

function dayHasNextDayClockOut(day) {
  return (day?.clock_events || []).some((evt) => {
    if (evt.event_type !== 'clock_out' || !evt.timestamp || !day?.date) return false;
    return String(evt.timestamp).slice(0, 10) > day.date;
  });
}

/** DS: tooltips must add context — skip when nothing beyond the cell is available. */
function getCalendarDayTooltipDetail(day) {
  if (!day) return null;

  const rows = [];
  const isLeave = day.composite_status === 'on_leave' && day.leave_type_name;

  if (isLeave) {
    rows.push({ label: 'Leave', value: day.leave_type_name });
  }

  if (day.first_clock_in) {
    rows.push({ label: 'First In', value: formatTimeString12Hour(day.first_clock_in), mono: true });
  }
  if (day.last_clock_out) {
    rows.push({
      label: 'Last Out',
      value: formatTimeString12Hour(day.last_clock_out),
      mono: true,
      badge: dayHasNextDayClockOut(day) ? '+1d' : null,
    });
  }

  if (day.beyond_schedule_hours > 0) {
    rows.push({
      label: 'Extra Hrs',
      value: formatHoursMinutes(day.beyond_schedule_hours),
      mono: true,
      emphasis: true,
    });
  }

  const projects = [...new Set(
    (day.clock_events || []).map((e) => e.project_name).filter(Boolean)
  )];
  if (projects.length === 1) {
    rows.push({ label: 'Project', value: projects[0] });
  } else if (projects.length > 1) {
    rows.push({ label: 'Projects', value: `${projects.length} sites` });
  }

  const outsideCount = (day.clock_events || []).filter((e) => e.within_geofence === false).length;
  if (outsideCount > 0) {
    rows.push({
      label: 'Geofence',
      value: outsideCount === 1 ? '1 event outside' : `${outsideCount} events outside`,
      warning: true,
    });
  }

  if (day.composite_status === 'unaccounted') {
    rows.push({ label: 'Note', value: 'No clock-in on a scheduled working day', warning: true });
  } else if (
    ['work_from_home', 'external_activity', 'on_course'].includes(day.composite_status)
    && day.hours_source === 'schedule'
    && !day.first_clock_in
  ) {
    rows.push({ label: 'Hours', value: 'From approved arrangement (no clock)' });
  } else if (day.composite_status === 'nightwork_rest_day') {
    rows.push({ label: 'Note', value: 'Recovery day after nightwork' });
  } else if (day.composite_status === 'public_holiday' && !day.first_clock_in) {
    rows.push({ label: 'Note', value: 'Public holiday — no attendance expected' });
  }

  return rows.length > 0 ? rows : null;
}

function tooltipPositionClass(colIndex, rowIndex) {
  // First calendar row: open downward so the card header does not clip the tip.
  const axis = rowIndex === 0 ? 'bottom' : 'top';
  if (colIndex === 0) return `tooltip-${axis}-start`;
  if (colIndex === 6) return `tooltip-${axis}-end`;
  return `tooltip-${axis}`;
}

function CalendarDayTooltipRows({ rows }) {
  return (
    <div className="space-y-1">
      {rows.map((row) => (
        <div key={row.label} className="flex items-start justify-between gap-3">
          <span className="text-gray-400 flex-shrink-0">{row.label}</span>
          <span
            className={[
              'text-right',
              row.mono ? 'font-mono tabular-nums' : '',
              row.warning ? 'text-amber-300 font-medium' : row.emphasis ? 'text-amber-200 font-medium' : 'text-white',
            ].filter(Boolean).join(' ')}
          >
            {row.value}
            {row.badge && (
              <span className="ml-1.5 inline-flex items-center rounded-full bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-medium text-amber-200 ring-1 ring-inset ring-amber-400/30">
                {row.badge}
              </span>
            )}
          </span>
        </div>
      ))}
    </div>
  );
}

export function CalendarGrid({ dailyStatus }) {
  const rows = useMemo(() => {
    if (!dailyStatus?.length) return [];
    const first = new Date(`${dailyStatus[0].date}T12:00:00`);
    const startDow = new Date(first.getFullYear(), first.getMonth(), 1).getDay();
    const offset = (startDow + 6) % 7;
    const cells = [...Array(offset).fill(null), ...dailyStatus];
    const result = [];
    for (let i = 0; i < cells.length; i += 7) result.push(cells.slice(i, i + 7));
    return result;
  }, [dailyStatus]);

  return (
    <div>
      <div className="grid grid-cols-7 mb-1">
        {DOW.map((d) => (
          <div key={d} className="text-xs text-gray-400 text-center font-medium py-1">{d}</div>
        ))}
      </div>
      {rows.map((row, ri) => (
        <div key={ri} className="grid grid-cols-7 gap-0.5 mb-0.5">
          {row.map((day, ci) => {
            if (!day) return <div key={ci} />;

            const isLeave = day.composite_status === 'on_leave' && day.leave_type_name;
            const leaveStyle = isLeave ? getLeaveColorStyle(day.leave_type_name) : null;
            const cfg = STATUS_CONFIG[day.composite_status] || STATUS_CONFIG.non_working_day;

            const bg = leaveStyle ? leaveStyle.light : cfg.light;
            const textColor = leaveStyle ? leaveStyle.text : cfg.text;
            const borderColor = (leaveStyle ? leaveStyle.color : cfg.color) + '50';
            const statusLabel = isLeave ? getLeaveTypeAbbreviation(day.leave_type_name) : cfg.label;
            const dayNum = new Date(`${day.date}T12:00:00`).getDate();
            const tooltipRows = getCalendarDayTooltipDetail(day);
            const tipPos = tooltipPositionClass(ci, ri);

            return (
              <div
                key={day.date}
                className={`tooltip-container w-full ${tooltipRows ? 'cursor-help' : ''}`}
                style={{ display: 'block' }}
              >
                <div
                  style={{ backgroundColor: bg, borderColor }}
                  className="rounded border p-1 min-h-[3.25rem] flex flex-col items-center"
                >
                  <span className="text-xs font-medium" style={{ color: textColor }}>{dayNum}</span>
                  <span className="text-[10px] mt-0.5 leading-tight text-center" style={{ color: textColor }}>
                    {statusLabel}
                  </span>
                  {(day.effective_hours ?? day.total_hours) != null && (
                    <span className="text-[9px] text-gray-500 mt-0.5">
                      {formatHoursMinutes(day.effective_hours ?? day.total_hours)}
                    </span>
                  )}
                </div>
                {tooltipRows && (
                  <div className={`tooltip-content ${tipPos}`} style={{ minWidth: '220px' }}>
                    <div className="tooltip-arrow" />
                    <div className="space-y-1.5 text-xs">
                      <p className="font-semibold text-white">{formatCalendarTooltipDate(day.date)}</p>
                      <CalendarDayTooltipRows rows={tooltipRows} />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {row.length < 7 && [...Array(7 - row.length)].map((_, ci) => <div key={`pad-${ci}`} />)}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Calendar legend (with leave-type swatches when applicable)
// ---------------------------------------------------------------------------

export function CalendarLegend({ dailyStatus }) {
  const baseStatuses = ['on_site', 'work_from_home', 'external_activity', 'on_course', 'on_leave', 'unaccounted', 'non_working_day'];

  const leaveTypes = useMemo(() => {
    if (!dailyStatus) return [];
    const seen = new Set();
    dailyStatus.forEach(d => {
      if (d.composite_status === 'on_leave' && d.leave_type_name && !seen.has(d.leave_type_name)) {
        seen.add(d.leave_type_name);
      }
    });
    return Array.from(seen);
  }, [dailyStatus]);

  return (
    <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1">
      {baseStatuses.filter(s => s !== 'on_leave').map((s) => {
        const cfg = STATUS_CONFIG[s];
        return (
          <span key={s} className="inline-flex items-center gap-1 text-xs text-gray-500">
            <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: cfg.color }} />
            {cfg.label}
          </span>
        );
      })}
      {leaveTypes.length > 0 ? (
        leaveTypes.map((lt) => {
          const style = getLeaveColorStyle(lt);
          return (
            <span key={lt} className="inline-flex items-center gap-1 text-xs text-gray-500">
              <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: style?.color || '#d97706' }} />
              {getLeaveTypeAbbreviation(lt)} ({lt})
            </span>
          );
        })
      ) : (
        <span className="inline-flex items-center gap-1 text-xs text-gray-500">
          <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: STATUS_CONFIG.on_leave.color }} />
          {STATUS_CONFIG.on_leave.label}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Clock pattern summary
// ---------------------------------------------------------------------------

export function ClockPatternSummary({ pattern }) {
  if (!pattern) return null;
  const { median_clock_in, iqr_clock_in, median_clock_out, iqr_clock_out, data_points } = pattern;

  if (!median_clock_in && !median_clock_out) {
    return (
      <p className="text-sm text-gray-400 italic">
        Not enough present days to compute a clock pattern (minimum 2 required).
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <p className="text-xs text-gray-500 mb-1 font-medium">Typical Clock-in</p>
          <p className="text-2xl font-bold text-gray-900">{median_clock_in ? formatTimeString12Hour(median_clock_in) : '\u2014'}</p>
          {iqr_clock_in && (
            <p className="text-xs text-gray-400 mt-0.5">Usual range: {formatTimeString12Hour(iqr_clock_in[0])} – {formatTimeString12Hour(iqr_clock_in[1])}</p>
          )}
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-1 font-medium">Typical Clock-out</p>
          <p className="text-2xl font-bold text-gray-900">{median_clock_out ? formatTimeString12Hour(median_clock_out) : '\u2014'}</p>
          {iqr_clock_out && (
            <p className="text-xs text-gray-400 mt-0.5">Usual range: {formatTimeString12Hour(iqr_clock_out[0])} – {formatTimeString12Hour(iqr_clock_out[1])}</p>
          )}
        </div>
      </div>
      <p className="text-xs text-gray-400">
        Based on {data_points} present day{data_points !== 1 ? 's' : ''} this month
        (WFH / leave / nightwork excluded from pattern analysis)
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Weekly hours chart
// ---------------------------------------------------------------------------

export function WeeklyHoursChart({ weeklyHours, isWorker }) {
  if (!weeklyHours?.length) return null;
  const beyondKey = isWorker ? 'OT' : 'Extended';
  const beyondColor = isWorker ? '#0ea5e9' : '#f59e0b';

  const data = weeklyHours.map((w) => ({
    week: formatWeekRange(w.week_start),
    Expected: parseFloat(w.expected_hours.toFixed(1)),
    Actual: parseFloat(w.actual_hours.toFixed(1)),
    [beyondKey]: parseFloat(w.beyond_hours.toFixed(1)),
  }));

  return (
    <div>
      <h5 className="text-xs font-semibold text-gray-600 mb-2">Weekly Hours</h5>
      <ResponsiveContainer width="100%" height={180}>
        <BarChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="week" tick={{ fontSize: 10 }} />
          <YAxis tick={{ fontSize: 10 }} unit="h" />
          <Tooltip formatter={(v) => formatHoursMinutes(v)} />
          <Bar dataKey="Expected" fill="#e5e7eb" radius={[3, 3, 0, 0]} />
          <Bar dataKey="Actual" fill="#1e3a8a" radius={[3, 3, 0, 0]} />
          <Bar dataKey={beyondKey} fill={beyondColor} radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
      <p className="text-xs text-gray-400 mt-1 text-center">
        {beyondKey} = time past scheduled end
        {isWorker ? ' (OT-entitled \u2014 compliance metric)' : ' (no OT entitlement \u2014 burnout signal)'}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Leave balance cards
// ---------------------------------------------------------------------------

export function LeaveBalanceCards({ balances }) {
  if (!balances?.length) {
    return <p className="text-sm text-gray-400 italic">No leave balance data for current year.</p>;
  }
  return (
    <div className="flex flex-wrap gap-2">
      {balances.map((lb) => {
        const pool =
          lb.total_available != null && lb.total_available > 0
            ? lb.total_available
            : lb.entitled > 0
              ? lb.entitled
              : 0;
        const pct = pool > 0 ? lb.used / pool : 0;
        const barColor = pct >= 0.9 ? 'bg-red-400' : pct >= 0.7 ? 'bg-amber-400' : 'bg-emerald-400';
        return (
          <div key={lb.leave_type} className="flex-1 min-w-[130px] bg-gray-50 rounded-xl border border-gray-200 p-3">
            <p className="text-xs font-medium text-gray-700 truncate">{lb.leave_type}</p>
            <div className="mt-1 w-full bg-gray-200 rounded-full h-1.5">
              <div
                className={`h-1.5 rounded-full ${barColor}`}
                style={{ width: `${Math.min(100, pct * 100)}%` }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {lb.used} / {pool} used
              <span className="ml-1 text-gray-400">({lb.remaining} remaining)</span>
            </p>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Compliance alerts
// ---------------------------------------------------------------------------

export function ComplianceAlerts({ alerts }) {
  if (!alerts) return null;
  const { rest_day, ot_monthly } = alerts;
  if (!rest_day && !ot_monthly) {
    return (
      <p className="text-sm text-green-600 font-medium flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
        No active compliance concerns.
      </p>
    );
  }
  return (
    <div className="space-y-2">
      {rest_day && (
        <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-200">
          <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-red-800 capitalize">
              Rest Day: {rest_day.compliance_status.replace(/_/g, ' ')}
            </p>
            <p className="text-red-600 text-xs mt-0.5">
              {rest_day.consecutive_working_days} consecutive working days
              {rest_day.streak_start_date ? ` since ${formatDateShort(rest_day.streak_start_date)}` : ''}
            </p>
          </div>
        </div>
      )}
      {ot_monthly && (
        <div className="flex items-start gap-2 p-3 rounded-xl bg-orange-50 border border-orange-200">
          <AlertTriangle className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-orange-800 capitalize">
              OT: {ot_monthly.compliance_status.replace(/_/g, ' ')}
            </p>
            <p className="text-orange-600 text-xs mt-0.5">
              {formatHoursMinutes(ot_monthly.total_ot_hours)} OT in {ot_monthly.year_month ? formatMonthYear(ot_monthly.year_month + '-01') : ot_monthly.year_month}
              {' '}(MOM limit: 72h/month)
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Attendance exceptions
// ---------------------------------------------------------------------------

export function AttendanceExceptions({ exceptions }) {
  if (!exceptions?.length) {
    return <p className="text-sm text-gray-400 italic">No attendance exceptions this month.</p>;
  }
  return (
    <div className="space-y-1">
      {exceptions.map((ex, i) => {
        // The three absent-family types share the same red treatment;
        // missing_clockout (and any future amber-class type) gets amber.
        const isAbsentFamily = (
          ex.exception_type === 'absent_no_leave'
          || ex.exception_type === 'absent_am'
          || ex.exception_type === 'absent_pm'
        );
        const labelMap = {
          absent_no_leave: 'Unexplained Absence',
          absent_am: 'Absent (AM)',
          absent_pm: 'Absent (PM)',
          missing_clockout: 'Missing Clock-out',
        };
        return (
          <div key={i} className="flex items-center gap-3 text-sm py-1.5 border-b border-gray-100 last:border-0">
            <span className="text-gray-500 text-xs w-24 flex-shrink-0">{formatDateShort(ex.exception_date)}</span>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
              isAbsentFamily
                ? 'bg-red-50 text-red-700 ring-1 ring-red-600/20'
                : 'bg-amber-50 text-amber-700 ring-1 ring-amber-600/20'
            }`}>
              {labelMap[ex.exception_type] || 'Exception'}
            </span>
            <span className={`text-xs ml-auto capitalize ${
              ex.exception_status === 'resolved'  ? 'text-emerald-600' :
              ex.exception_status === 'dismissed' ? 'text-gray-400' : 'text-amber-600'
            }`}>
              {ex.exception_status}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Work arrangement summary
// ---------------------------------------------------------------------------

const WORK_ARRANGEMENT_ITEMS = [
  { key: 'wfh_count',       label: 'WFH',      leaveTypeName: 'work from home' },
  { key: 'ea_count',        label: 'External',  leaveTypeName: 'external activity' },
  { key: 'on_course_count', label: 'On Course', leaveTypeName: 'on course' },
];

export function WorkArrangementSummary({ summary }) {
  if (!summary) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {WORK_ARRANGEMENT_ITEMS.map(({ key, label, leaveTypeName }) => (
        <span
          key={key}
          className={`inline-flex items-center rounded-full font-medium ring-1 ring-inset px-2.5 py-0.5 text-xs ${getLeaveTypeBadgeClass(leaveTypeName)}`}
        >
          <span className="font-bold mr-1">{summary[key] ?? 0}</span>
          {label}
        </span>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Month navigator — DEPRECATED: use MonthNavigator from components/UI/MonthNavigator.js
// Kept temporarily for backward compatibility; all consumers should migrate.
// ---------------------------------------------------------------------------

export { default as MonthNav } from '../../UI/MonthNavigator';
